/* eslint-disable no-new */
import { watch, watchSubscriptions } from "~/state/util";
import { Unsubscribe } from "redux";
import { IPOIDescription, IPosition, ISubscriptionWithDescription } from "~/util/providers/share-position/interfaces";
import { store } from "~/state/store";
import { selectActiveSubscription, selectSubscriptions } from "~/state/share-position/selectors";
import { filterSubscriptions, routeParts2Position, STALE_TIMEOUT } from "~/share-position2/utils";
import { addListener, removeListener } from "~/lib/signals";
import WasmStoreMarker from "~/wasm/store/wasm-store-marker";
import { WASM_ZINDEX } from "./const";
import { CONFIG } from "~/config";
import { setCoordinates, setFit } from "~/state/map/state";
import WasmStoreGeometry from "~/wasm/store/wasm-store-geometry";
import { coordsToString, stringToCoords } from "~/util/mirek";
import { convertSmapStyleToWasmGeometryStyleHash } from "./utils";
import { getMarkerImgFrompoi } from "~/util/wasm";

interface IMapSubscription {
	avatar: WasmStoreMarker;
	geometryGone: WasmStoreGeometry;
	geometryToGo: WasmStoreGeometry;
	start?: WasmStoreMarker;
	destination?: WasmStoreMarker;
}

const TOGO_LINE_STYLE = {
	width: 8,
	color: "#0033ff",
	opacity: 0.6,
	outlineWidth: 2,
	outlineColor: "white",
	outlineOpacity: 0.4,
	style: JAK.Vector.STYLE_SOLID,
	endCap: JAK.Vector.END_STYLE_ROUND,
};

const GONE_LINE_STYLE = {
	width: 8,
	color: "#454c54",
	opacity: 0.6,
	outlineWidth: 2,
	outlineColor: "white",
	outlineOpacity: 0.4,
	style: JAK.Vector.STYLE_SOLID,
	endCap: JAK.Vector.END_STYLE_ROUND,
};

const VISUAL_OPPOSITES = {
	"standard": "inactive",
	"position-gray-active": "position-gray-inactive",
	"inactive": "standard",
	"position-gray-inactive": "position-gray-active",
};

/** zoom, na který se nastaví mapa při aktivaci sledování, které nemá geometrii a tedy se nedělá bestfit */
const NO_GEOMETRY_ZOOM = 20;

function createAvatar(subscription: ISubscriptionWithDescription, position: IPosition, optsArg?: { radius?: number; order?: number; }) {
	const opts = {
		radius: 0,
		order: 0,
		...optsArg,
	};
	const avatar = new WasmStoreMarker({
		type: "azimuth" in subscription.position ? "position-share-arrow" : "position-share",
		lon: position.lon,
		lat: position.lat,
		visual: opts.order || (subscription.sharingStatus === "active" && new Date(subscription.positionTime).getTime() + STALE_TIMEOUT >= new Date().getTime()) ? "inactive" : "position-gray-inactive",
		angle: "azimuth" in subscription.position
			? subscription.position.azimuth
			: 0,
		zIndex: WASM_ZINDEX.marker.sharePosition,
		interactionType: "click-hover",
		customData: {
			url: subscription.user.avatarUrl,
			...opts,
			subscriptionId: subscription.subscriptionId,
		},
		visible: subscription.attributes.showOnMap || Mapy.getComponent("sharePosition").isActive(),
	});

	return avatar;
}

export default class WasmSharePosition {
	#storeUnsubscribeSharePosition: Unsubscribe;

	#storeUnsubscribeSharePositionActive: Unsubscribe;

	#storeUnsubscribeSharePositionHover: Unsubscribe;

	#subscriptions: {
		[key: string]: IMapSubscription;
	};

	#sharepositionActivatedListener: string;

	#sharepositionDeactivatedListener: string;

	constructor() {
		this.#subscriptions = {};

		// precteme napoprve ze store
		const storeState = store.getState();
		const subscriptions = selectSubscriptions(storeState);
		const activeSubsciption = selectActiveSubscription(storeState);

		this.#sharePositionVisualize(subscriptions, activeSubsciption, activeSubsciption);

		// nahodime sledovani stavu ve store
		const wtSharePositionSubscriptions = watch(store.getState, "sharePosition.subscriptions", watchSubscriptions);
		const wtSharePositionActiveSubscription = watch<string>(store.getState, "sharePosition.activeSubscription");
		const wtSharePositionHoverSubscription = watch<string>(store.getState, "sharePosition.hoverSubscription");

		this.#storeUnsubscribeSharePosition = store.subscribe(wtSharePositionSubscriptions(newSubscriptions => {
			const activeSub = selectActiveSubscription(store.getState());

			this.#sharePositionVisualize(newSubscriptions, activeSub, activeSub);
		}));
		this.#storeUnsubscribeSharePositionActive = store.subscribe(wtSharePositionActiveSubscription((activeSub, oldActiveSub) => {
			const subs = selectSubscriptions(store.getState());

			this.#sharePositionVisualize(subs, activeSub, oldActiveSub);
		}));
		this.#storeUnsubscribeSharePositionHover = store.subscribe(wtSharePositionHoverSubscription((hoverSub, oldHoverSub) => {
			if (oldHoverSub) {
				this.#subscriptions[oldHoverSub]?.avatar.update({
					visual: VISUAL_OPPOSITES[this.#subscriptions[oldHoverSub]?.avatar.data.visual],
					bringToTheFront: false,
				});
				//this.#subscriptions[oldHoverSub]?.avatar.removeCard();
			}

			if (hoverSub) {
				this.#subscriptions[hoverSub]?.avatar.update({
					visual: VISUAL_OPPOSITES[this.#subscriptions[hoverSub]?.avatar.data.visual],
					bringToTheFront: true,
				});
				//this.#subscriptions[hoverSub]?.avatar.showCard();
			}
		}));

		this.#sharepositionActivatedListener = addListener(this, "shareposition-activated", "sharepositionActivated");
		this.#sharepositionDeactivatedListener = addListener(this, "shareposition-deactivated", "sharepositionDeactivated");
	}

	destroy() {
		// zruseni sledovani pozice
		this.#storeUnsubscribeSharePosition();
		this.#storeUnsubscribeSharePositionActive();
		this.#storeUnsubscribeSharePositionHover();
		this.#clearSharePosition();

		removeListener(this.#sharepositionActivatedListener);
		removeListener(this.#sharepositionDeactivatedListener);
	}

	#sharePositionVisualize(subscriptions: Array<ISubscriptionWithDescription>, activeId: string, oldActiveId: string) {
		const filteredSubscriptions = filterSubscriptions(subscriptions);

		// pokud je nove aktivni, schovame geometrii a radius toho stareho
		if (activeId !== oldActiveId) {
			if (oldActiveId in this.#subscriptions) {
				const { avatar, geometryGone, geometryToGo, start, destination } = this.#subscriptions[oldActiveId];
				const oldData = avatar.data;

				avatar.destroy();
				this.#subscriptions[oldActiveId].avatar = new WasmStoreMarker({
					...oldData,
					customData: {
						...oldData.customData,
						// schovame radius
						radius: 0,
					},
				});

				geometryGone.update({
					visible: false,
				});
				geometryToGo.update({
					visible: false,
				});
				start?.update({
					visible: false,
				});
				destination?.update({
					visible: false,
				});
			}
		}

		// aktualizujeme, co je videt a co uz zahazujeme
		if (filteredSubscriptions.length) {
			const newSubscriptions: { [key: string]: IMapSubscription; } = {};

			filteredSubscriptions.forEach((subscription, subscriptionIndex) => {
				// mame takovou subscription uz ted? tak jen updatujeme jeji data a poradi ve vypisu
				if (subscription.subscriptionId in this.#subscriptions) {
					const { avatar, geometryGone, geometryToGo, start, destination } = this.#subscriptions[subscription.subscriptionId];

					// geometrie
					const [coordsGone, coordsToGo] = routeParts2Position(subscription.routeParts, subscription.routeInfo?.routeIndex || 0, subscription.routeInfo?.segmentIndex || 0);

					geometryGone.update({
						strPoints: [coordsToString(coordsGone)],
					});
					geometryToGo.update({
						strPoints: [coordsToString(coordsToGo)],
					});

					// avatar
					const geometryCoords = coordsGone.length ? coordsGone[coordsGone.length - 1] : coordsToGo.length ? coordsToGo[0] : null;
					const useGeometryCoords = subscription.routeInfo && "offTheRoad" in subscription.routeInfo && !subscription.routeInfo.offTheRoad && !!geometryCoords;
					const lon = useGeometryCoords ? geometryCoords[0] : subscription.position?.lon || 0;
					const lat = useGeometryCoords ? geometryCoords[1] : subscription.position?.lat || 0;
					const position: IPosition = {
						lon,
						lat,
						accuracy: subscription.position?.accuracy || 0,
						...subscription.routeInfo?.destinationReached || subscription.sharingStatus === "canceled" ? {} : { azimuth: subscription.position?.azimuth },
					};

					avatar.update({
						lon: position.lon,
						lat: position.lat,
						...subscription.sharingType === "device" ? { order: subscriptionIndex + 1 } : {},
						..."azimuth" in position
							? {
								angle: position.azimuth,
							}
							: {},
						visible: subscription.attributes.showOnMap || Mapy.getComponent("sharePosition").isActive(),
					});

					// start a destination
					if (coordsGone.length || coordsToGo.length) {
						// start
						start?.update({
							lon: (coordsGone.length ? coordsGone[0] : coordsToGo[0])[0],
							lat: (coordsGone.length ? coordsGone[0] : coordsToGo[0])[1],
						});

						// cil
						destination?.update({
							lon: (coordsToGo.length ? coordsToGo[coordsToGo.length - 1] : coordsGone[coordsGone.length - 1])[0],
							lat: (coordsToGo.length ? coordsToGo[coordsToGo.length - 1] : coordsGone[coordsGone.length - 1])[1],
						});
					}


					// dame si item do noveho objektu a ze stareho odstranime
					newSubscriptions[subscription.subscriptionId] = this.#subscriptions[subscription.subscriptionId];
					delete this.#subscriptions[subscription.subscriptionId];
				// je to nova vec, vyrobime ji
				} else {
					// geometrie
					const [coordsGone, coordsToGo] = routeParts2Position(subscription.routeParts, subscription.routeInfo?.routeIndex || 0, subscription.routeInfo?.segmentIndex || 0);
					const geometryGone = new WasmStoreGeometry({
						type: "custom",
						strPoints: [coordsToString(coordsGone)],
						customStyleHash: {
							standard: convertSmapStyleToWasmGeometryStyleHash(GONE_LINE_STYLE, true),
						},
						interactionType: "none",
						zIndex: WASM_ZINDEX.geometry.sharePosition,
						visible: false,
					});
					const geometryToGo = new WasmStoreGeometry({
						type: "custom",
						strPoints: [coordsToString(coordsToGo)],
						customStyleHash: {
							standard: convertSmapStyleToWasmGeometryStyleHash(TOGO_LINE_STYLE, true),
						},
						interactionType: "none",
						zIndex: WASM_ZINDEX.geometry.sharePosition,
						visible: false,
					});

					// avatar
					const geometryCoords = coordsGone.length ? coordsGone[coordsGone.length - 1] : coordsToGo.length ? coordsToGo[0] : null;
					const useGeometryCoords = subscription.routeInfo && "offTheRoad" in subscription.routeInfo && !subscription.routeInfo.offTheRoad && !!geometryCoords;
					const lon = useGeometryCoords ? geometryCoords[0] : subscription.position?.lon ?? 0;
					const lat = useGeometryCoords ? geometryCoords[1] : subscription.position?.lat ?? 0;
					const position: IPosition = {
						lon,
						lat,
						accuracy: subscription.position?.accuracy || 0,
						...subscription.routeInfo?.destinationReached || subscription.sharingStatus === "canceled" ? {} : { azimuth: subscription.position?.azimuth },
					};
					const avatar = createAvatar(subscription, position, {
						...subscription.sharingType === "device" ? { order: subscriptionIndex + 1 } : {},
						radius: "accuracy" in subscription.position
							? subscription.position.accuracy
							: 0,
					});

					// start
					let start = null;

					if (coordsGone.length || coordsToGo.length) {
						const startPosition = coordsGone.length ? coordsGone[0] : coordsToGo[0];

						start = new WasmStoreMarker({
							type: "little",
							lon: startPosition[0],
							lat: startPosition[1],
							visual: "standard",
							zIndex: WASM_ZINDEX.marker.sharePosition,
							interactionType: "none",
							visible: false,
						});
					}

					// cil
					let destination = null;

					// tahle cast je z vetsiny ukradena z /search/markers/index.ts a je to dost hruza - nicmene takhle tedy pracujeme s card a zmenou velikosti markeru
					if (subscription.routeParts?.length && subscription.routeParts.at(-1).poi.description) {
						const poi: IPOIDescription = {
							...subscription.routeParts.at(-1).poi.description,
						};
						const markerImg = poi.typeId
							// @ts-ignore
							? getMarkerImgFrompoi(poi)
							: "";

						destination = new WasmStoreMarker({
							type: markerImg ? "big" : "big-universal",
							lon: poi.mark.lon,
							lat: poi.mark.lat,
							visual: "standard",
							zIndex: WASM_ZINDEX.marker.sharePosition,
							interactionType: "click-hover",
							customData: {
								...markerImg ? { url: markerImg } : {},
								poi: {
									source: poi.source,
									id: poi.id,
								},
							},
							visible: false,
						});
					}

					// zapamatujeme si vsechno pro budouci uziti
					newSubscriptions[subscription.subscriptionId] = { avatar, geometryGone, geometryToGo, start, destination };
				}
			});

			// co zustalo v this.#subscriptions, to uz ve skutecnosti nechceme
			for (const subscriptionId of Object.keys(this.#subscriptions)) {
				this.#destroySubscription(subscriptionId);
			}

			// nove aktualni si zapamatujeme
			this.#subscriptions = newSubscriptions;
		} else {
			// zrusime vsechno, co jsme mozna dosud ukazovali
			for (const subscriptionId of Object.keys(this.#subscriptions)) {
				this.#destroySubscription(subscriptionId);
			}

			this.#subscriptions = {};
		}

		// pokud je nove aktivni, ukazeme jeji geometrii
		if (activeId !== oldActiveId) {
			if (activeId in this.#subscriptions) {
				const { avatar, geometryGone, geometryToGo, start, destination } = this.#subscriptions[activeId];

				geometryGone.update({
					visible: true,
				});
				geometryToGo.update({
					visible: true,
				});
				start?.update({
					visible: true,
				});
				destination?.update({
					visible: true,
				});

				try {
					const sceneCoords = [
						...geometryGone.data.strPoints.length ? stringToCoords(Array.isArray(geometryGone.data.strPoints[0]) ? geometryGone.data.strPoints[0][0] : geometryGone.data.strPoints[0]) : [],
						...geometryToGo.data.strPoints.length ? stringToCoords(Array.isArray(geometryToGo.data.strPoints[0]) ? geometryToGo.data.strPoints[0][0] : geometryToGo.data.strPoints[0]) : [],
					];

					// mame geometrie - udelame bestfit
					if (sceneCoords.length) {
						store.dispatch(setFit(sceneCoords));
					// nemame geometrie, proste vystredime na avatara
					} else {
						const { zoom } = store.getState().map;

						store.dispatch(setCoordinates({
							longitude: avatar.data.lon,
							latitude: avatar.data.lat,
							zoom: NO_GEOMETRY_ZOOM,
							animate: !CONFIG.inFrame && NO_GEOMETRY_ZOOM >= zoom - 1 && NO_GEOMETRY_ZOOM <= zoom + 1,
						}));
					}

					Mapy.getComponent("sharePosition").activate();
					Mapy.getComponent("history").save();
				} catch (ex) {
					//
				}
			}
		}
	}

	#clearSharePosition() {
		for (const subscriptionId of Object.keys(this.#subscriptions)) {
			this.#destroySubscription(subscriptionId);
		}

		this.#subscriptions = {};
	}

	#destroySubscription(subscriptionId: string) {
		const { avatar, geometryGone, geometryToGo, start, destination } = this.#subscriptions[subscriptionId];

		avatar.destroy();
		geometryGone.destroy();
		geometryToGo.destroy();
		start?.destroy();
		destination?.destroy();
		delete this.#subscriptions[subscriptionId];
	}

	sharepositionActivated() {
		const { activeSubscription } = store.getState().sharePosition;

		for (const subscriptionItem of Object.entries(this.#subscriptions)) {
			const subscriptionId = subscriptionItem[0];
			const { avatar } = subscriptionItem[1];

			avatar.update({
				visible: true,
				bringToTheFront: activeSubscription === subscriptionId,
			});
		}
	}

	sharepositionDeactivated() {
		const storeState = store.getState();
		const subscriptions = selectSubscriptions(storeState);

		subscriptions.forEach(subscription => {
			if (!subscription.attributes.showOnMap && subscription.subscriptionId in this.#subscriptions) {
				const { avatar } = this.#subscriptions[subscription.subscriptionId];

				avatar.update({
					visible: false,
				});
			}
		});
	}
}
